import { ApiRequestError, Skeleton } from '@h2hspark/ui';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageBackLink } from '@/member/page-back-link';

import { useAttendeeProfile } from '../attendee-preview/api';
import { AttendeeDetailCard } from '../attendee-preview/attendee-detail-card';
import {
  AttendeeDetailCloseControl,
  AttendeeDetailOverlay,
} from '../attendee-preview/attendee-detail-dialog';
import { initialsOf, roleAtCompany } from '../attendee-preview/constants';
import { prefilledMatchMessage } from '../connections/constants';

import { useMyMatchSet } from './api';
import { WHY_YOU_SHOULD_MEET_LABEL } from './constants';
import type { MatchSuggestion } from './types';

const BACK_TO_MATCHES_LABEL = 'Back to your matches';

/**
 * `MEM-025` AC-6 — match detail: the person, then **Why you should meet**, **About**, and the
 * actions.
 *
 * ## It is `Web App.dc.html` §2.16's card since 2026-09-12, and that is what the prototype draws
 *
 * The 10 September file has **no separate match-detail screen.** §3.8's match card head opens the
 * same `personOpen` overlay an attendee row opens, and that overlay's *"Here to talk about"* box
 * renders `a.quote || a.why` — the person's conversation starter where they wrote one, and the
 * **match narrative** where they did not. One card for a person inside an event, whichever list
 * you reached them from.
 *
 * So this screen is `AttendeeDetailCard`, with the narrative passed in. What it keeps that §2.16
 * folds away is the narrative's **own label**: `MEM-025` AC-6 fixes three sections and names
 * *"Why you should meet"* as one of them, and the sentence is the match set's rather than the
 * person's — collapsing the two into one unlabelled box would attribute matching's words to them.
 * So the card draws four regions here and three on an attendee: the narrative, then the quote,
 * then About, then Interests. **Called out in the pull request.**
 *
 * Until that day this screen was its own transcription of the old phone overlay: a 200px gradient
 * band, two full-width stacked buttons and a Connect control holding its state in `React.useState`
 * — the exact defect Sumit's ruling 29 had already fixed on the attendee profile and on the cards,
 * so a member who sent a request from here read *Connect* again on their next visit. It now shares
 * the one served `relationship` control with them (ruling 65).
 *
 * ## Where the sections come from, and why that is two calls
 *
 * The **narrative** is matching's, stored with the pair, and it is read from the match set. The
 * **person** — About, Interests, the conversation starter, the meet-list flag and the relationship
 * — is the attendee profile, read from `attendee-preview` behind its own gate.
 *
 * That is deliberate rather than incidental. A match detail that served its own copy of profile
 * data would be a second place a member's disclosure rules are applied, and the second place is
 * always the one that goes stale. One member's profile has one rule and one implementation of it;
 * matching contributes the sentence only.
 *
 * A consequence worth stating: a matched member whose profile did not resolve is named on the card
 * and their detail cannot be opened at all — `MEM-025` AC-3's person region is plain text on such
 * a card rather than a link to nowhere — and if this address is reached anyway, the narrative is
 * shown under their name without the person's own regions. See {@link MatchDetailBody}.
 */
export function MatchDetailPage(): React.ReactElement {
  const { eventId = '', attendeeRef = '' } = useParams<{ eventId: string; attendeeRef: string }>();

  return (
    <div className="-mx-4 -mt-6 md:mx-auto md:mt-0 md:w-full md:max-w-[460px]">
      <MatchDetailBody
        eventId={eventId}
        attendeeRef={attendeeRef}
        headingLevel="h1"
        cardClassName="md:rounded-xl md:border md:border-border-subtle md:shadow-sm"
        control={
          <PageBackLink
            variant="floating"
            to={`/app/events/${encodeURIComponent(eventId)}/matches`}
          >
            {BACK_TO_MATCHES_LABEL}
          </PageBackLink>
        }
      />
    </div>
  );
}

/** The same card, over the Matches page it was opened from (`MEM-056` AC-3). */
export function MatchDetailOverlay(): React.ReactElement {
  const { eventId = '', attendeeRef = '' } = useParams<{ eventId: string; attendeeRef: string }>();
  const navigate = useNavigate();
  const close = React.useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <AttendeeDetailOverlay title={BACK_TO_MATCHES_LABEL} onClose={close}>
      <MatchDetailBody
        eventId={eventId}
        attendeeRef={attendeeRef}
        headingLevel="h2"
        control={<AttendeeDetailCloseControl onClose={close} backLabel={BACK_TO_MATCHES_LABEL} />}
      />
    </AttendeeDetailOverlay>
  );
}

/** The two reads and their states, shared by the route and the overlay. */
function MatchDetailBody({
  eventId,
  attendeeRef,
  control,
  headingLevel,
  cardClassName,
}: {
  readonly eventId: string;
  readonly attendeeRef: string;
  readonly control: React.ReactNode;
  readonly headingLevel: 'h1' | 'h2';
  readonly cardClassName?: string | undefined;
}): React.ReactElement {
  const matchQuery = useMyMatchSet(eventId, { enabled: true });
  const profileQuery = useAttendeeProfile(eventId, attendeeRef === '' ? null : attendeeRef, {
    enabled: true,
  });

  const suggestion = matchQuery.data?.matchSet?.suggestions.find(
    (candidate) => candidate.attendeeRef === attendeeRef,
  );
  const profile = profileQuery.data?.profile;

  if (matchQuery.isPending || profileQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[196px] w-full" />
        <div className="space-y-3 px-6">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  // The **match** is what this screen is; without it there is nothing to show, whatever the
  // profile said.
  if (suggestion === undefined) {
    const notFound =
      matchQuery.error instanceof ApiRequestError && matchQuery.error.statusCode === 404;
    return (
      <div className="space-y-4 p-6">
        <Link
          to={`/app/events/${encodeURIComponent(eventId)}/matches`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
          <span>Your matches</span>
        </Link>
        <h1 className="text-2xl">
          {notFound ? 'That match is not here' : 'We could not open that match'}
        </h1>
        <p className="text-sm text-muted-foreground">
          It may have been withdrawn, or the link may be wrong.
        </p>
      </div>
    );
  }

  const narrative = { label: WHY_YOU_SHOULD_MEET_LABEL, text: suggestion.narrative } as const;

  // The profile did not resolve — election (a) is off, or the read failed. The narrative is still
  // this member's to read, so the card is drawn from the suggestion with the person's own regions
  // absent, rather than the screen being replaced by an error about somebody it can name.
  if (profile === undefined) {
    return (
      <UnresolvedMatchCard
        suggestion={suggestion}
        narrative={narrative}
        control={control}
        headingLevel={headingLevel}
        className={cardClassName}
      />
    );
  }

  return (
    <AttendeeDetailCard
      eventId={eventId}
      profile={profile}
      narrative={narrative}
      /* `MEM-032` AC-4 / `MEM-MAT-010`: from a match, and only from a match, the composer opens
         pre-filled with the match context. */
      prefilledMessage={prefilledMatchMessage({
        firstName: suggestion.firstName,
        eventName: matchQuery.data?.matchSet?.eventName ?? null,
      })}
      control={control}
      headingLevel={headingLevel}
      className={cardClassName}
    />
  );
}

/**
 * The same card's header and narrative for a match whose profile did not resolve.
 *
 * It draws no actions, and that is not a shortcut: Save to Meet and Connect are both addressed by
 * `attendeeRef`, the reference exists here only because the route carried it, and the profile read
 * behind it refused. A control that posted against a reference the disclosure gate has just
 * declined would be the gate being worked around by the screen it protects.
 */
function UnresolvedMatchCard({
  suggestion,
  narrative,
  control,
  headingLevel,
  className,
}: {
  readonly suggestion: MatchSuggestion;
  readonly narrative: { readonly label: string; readonly text: string };
  readonly control: React.ReactNode;
  readonly headingLevel: 'h1' | 'h2';
  readonly className?: string | undefined;
}): React.ReactElement {
  const Heading = headingLevel;
  const name =
    suggestion.lastName === null
      ? suggestion.firstName
      : `${suggestion.firstName} ${suggestion.lastName}`;
  const line = roleAtCompany(suggestion);

  return (
    <div className={`overflow-hidden bg-background ${className ?? ''}`}>
      <div className="relative flex flex-col items-center bg-spark p-7 text-center text-white">
        {control}
        <span
          className="mb-3.5 inline-flex h-[76px] w-[76px] items-center justify-center rounded-pill bg-white font-brand text-[25px] font-extrabold text-brand shadow-md"
          aria-hidden
        >
          {initialsOf({ firstName: suggestion.firstName, lastName: suggestion.lastName ?? '' })}
        </span>
        <Heading className="mb-[3px] font-brand text-xl font-extrabold text-white">{name}</Heading>
        {line === null ? null : <p className="text-sm opacity-[.92]">{line}</p>}
      </div>
      <div className="p-6">
        <section className="mb-5">
          <h3 className="mb-2 font-brand text-2xs font-semibold uppercase tracking-caps text-text-subtle">
            {narrative.label}
          </h3>
          <p className="rounded-md bg-brand-50 px-[15px] py-[13px] text-sm leading-snug text-text-body">
            {narrative.text}
          </p>
        </section>
        <p className="rounded-md bg-muted p-3.5 text-sm text-muted-foreground">
          {name} has not shared a full profile for this event.
        </p>
      </div>
    </div>
  );
}
